/**
 * Lookbook section web component.
 * Desktop: main swiper + thumbnails. Mobile: main swiper only (no thumbnails).
 */
const ANNOUNCEMENT_OFFSET_VAR = '--lookbook-announcement-offset';

class Lookbook extends HTMLElement {
  constructor() {
    super();
    this.swiperDesktop = null;
    this.swiperMobile = null;
    this.thumbnailsSwiper = null;
    this.boundOnCtaClick = this.onCtaClick.bind(this);
    this.boundAutoplayToggle = this.autoplayToggle.bind(this);
    this._announcementBarResizeObserver = null;
    this._boundSetAnnouncementBarOffset = this._setAnnouncementBarOffset.bind(this);
  }

  connectedCallback() {
    this._setupAnnouncementBarOffset();
    this.init();
    this.addEventListener('click', this.boundOnCtaClick);
  }

  disconnectedCallback() {
    this._teardownAnnouncementBarOffset();
    this.removeEventListener('click', this.boundOnCtaClick);
    this.querySelectorAll('.lookbook__autoplay').forEach((btn) => {
      btn.removeEventListener('click', this.boundAutoplayToggle);
    });
    this.destroy();
  }

  _setAnnouncementBarOffset() {
    const el = document.querySelector('.announcement-bar-section');
    const px = el ? `${el.offsetHeight}px` : '0px';
    document.documentElement.style.setProperty(ANNOUNCEMENT_OFFSET_VAR, px);
  }

  _setupAnnouncementBarOffset() {
    this._setAnnouncementBarOffset();
    const ann = document.querySelector('.announcement-bar-section');
    if (ann && typeof ResizeObserver !== 'undefined') {
      this._announcementBarResizeObserver = new ResizeObserver(this._boundSetAnnouncementBarOffset);
      this._announcementBarResizeObserver.observe(ann);
    }
    window.addEventListener('resize', this._boundSetAnnouncementBarOffset);
  }

  _teardownAnnouncementBarOffset() {
    if (this._announcementBarResizeObserver) {
      this._announcementBarResizeObserver.disconnect();
      this._announcementBarResizeObserver = null;
    }
    window.removeEventListener('resize', this._boundSetAnnouncementBarOffset);
  }

  onCtaClick(e) {
    if (e.target.closest('.lookbook-slide__cta')) {
      e.stopPropagation();
    }
  }

  init() {
    if (typeof Swiper === 'undefined') {
      console.error('Lookbook: Swiper library not loaded.');
      return;
    }

    const thumbnailsEl = this.querySelector('.lookbook__thumbnails .swiper');
    const mainDesktopEl = this.querySelector('.lookbook-main-desktop');
    const mainMobileEl = this.querySelector('.lookbook-main-mobile');

    if (!mainDesktopEl || !mainMobileEl) return;

    const allowMousewheel = this.dataset.allowMousewheel !== 'false';
    const mouseSensitivity = parseInt(this.dataset.mouseSensitivity, 10);
    const thresholdDelta = Number.isNaN(mouseSensitivity) ? 40 : mouseSensitivity;
    const allowAutoplay = this.dataset.allowAutoplay === 'true';
    const autoplayDelay = parseInt(this.dataset.autoplayDelay, 10) || 5000;
    const playInLoop = this.dataset.playInLoop === 'true';

    const commonMainOptions = {
      direction: 'vertical',
      spaceBetween: 0,
      loop: playInLoop,
      mousewheel: {
        enabled: allowMousewheel,
        releaseOnEdges: true,
        thresholdDelta,
      },
      autoplay: allowAutoplay
        ? {
            delay: autoplayDelay,
            disableOnInteraction: true,
          }
        : false,
      keyboard: { enabled: true },
    };

    // Thumbnails swiper (desktop only; hidden on mobile via CSS).
    // Loop disabled on thumbs so order stays 1, 2, 3 and sync with main swiper is correct (Swiper thumbs + loop has known reverse-order issues).
    if (thumbnailsEl) {
      this.thumbnailsSwiper = new Swiper(thumbnailsEl, {
        spaceBetween: 4,
        slidesPerView: 'auto',
        freeMode: true,
        watchSlidesProgress: true,
        direction: 'vertical',
        loop: false,
        on: {
          slideChange: () => this.updatePagination(this.swiperDesktop),
        },
      });
    }

    // Desktop main swiper (with thumbnails)
    this.swiperDesktop = new Swiper(mainDesktopEl, {
      ...commonMainOptions,
      thumbs: thumbnailsEl && this.thumbnailsSwiper ? { swiper: this.thumbnailsSwiper } : undefined,
      on: {
        slideChange: (swiper) => this.updatePagination(swiper),
      },
    });

    // Mobile main swiper (no thumbnails). touchReleaseOnEdges lets user scroll to footer when on last slide.
    this.swiperMobile = new Swiper(mainMobileEl, {
      ...commonMainOptions,
      loop: false,
      touchReleaseOnEdges: true,
      on: {
        slideChange: (swiper) => this.updatePagination(swiper),
      },
    });

    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const activeSwiper = isMobile ? this.swiperMobile : this.swiperDesktop;
    this.updatePagination(activeSwiper);

    this.querySelectorAll('.lookbook__autoplay').forEach((btn) => {
      btn.addEventListener('click', this.boundAutoplayToggle);
    });
  }

  autoplayToggle() {
    const buttons = this.querySelectorAll('.lookbook__autoplay');
    if (!buttons.length) return;
    const isPlaying = buttons[0].classList.contains('lookbook__autoplay--playing');
    const swipers = [this.swiperDesktop, this.swiperMobile].filter(Boolean);
    const playLabel = window.accessibilityStrings?.playSlideshow ?? 'Play slideshow';
    const pauseLabel = window.accessibilityStrings?.pauseSlideshow ?? 'Pause slideshow';
    if (isPlaying) {
      swipers.forEach((s) => s.autoplay?.stop());
      buttons.forEach((btn) => {
        btn.classList.remove('lookbook__autoplay--playing');
        btn.classList.add('lookbook__autoplay--paused');
        btn.setAttribute('aria-label', playLabel);
      });
    } else {
      swipers.forEach((s) => s.autoplay?.start());
      buttons.forEach((btn) => {
        btn.classList.add('lookbook__autoplay--playing');
        btn.classList.remove('lookbook__autoplay--paused');
        btn.setAttribute('aria-label', pauseLabel);
      });
    }
  }

  updatePagination(swiper) {
    if (!swiper) return;
    const total = parseInt(this.dataset.totalSlides, 10) || 0;
    const realIndex =
      typeof swiper.realIndex === 'number' ? swiper.realIndex : parseInt(swiper.realIndex, 10) || 0;
    const currentIndex = Math.min(realIndex + 1, total || realIndex + 1);
    const currentEl = this.querySelector('.lookbook__counter-current');
    const totalEl = this.querySelector('.lookbook__counter-total');
    const counterEl = this.querySelector('.lookbook__pagination-counter');
    if (currentEl) currentEl.textContent = String(currentIndex);
    if (totalEl) totalEl.textContent = String(total);
    if (counterEl) counterEl.setAttribute('aria-label', `Slide ${currentIndex} of ${total}`);
    const sourceSwiper =
      this.thumbnailsSwiper && (swiper === this.swiperDesktop || swiper === this.thumbnailsSwiper)
        ? this.thumbnailsSwiper
        : swiper;
    this.updateThumbnailAriaCurrent(sourceSwiper);
  }

  updateThumbnailAriaCurrent(swiper) {
    const thumbnails = this.querySelectorAll('.lookbook__thumbnails .lookbook-thumbnail');
    if (!thumbnails.length) return;
    thumbnails.forEach((thumbnail, i) => {
      thumbnail.setAttribute('aria-current', i === swiper.realIndex ? 'true' : 'false');
    });
  }

  destroy() {
    if (this.swiperDesktop) {
      this.swiperDesktop.destroy(true, true);
      this.swiperDesktop = null;
    }
    if (this.swiperMobile) {
      this.swiperMobile.destroy(true, true);
      this.swiperMobile = null;
    }
    if (this.thumbnailsSwiper) {
      this.thumbnailsSwiper.destroy(true, true);
      this.thumbnailsSwiper = null;
    }
  }
}

customElements.define('lookbook-section', Lookbook);
